import { useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { invoiceConfirmApi } from '../features/invoiceConfirm/api.ts';
import { Icon } from '../components/Icon.tsx';
import { t } from '../lib/strings.ts';

// ── Signature pad ─────────────────────────────────────────────────────────
// Minimal canvas signature pad using pointer events. No external dependency.

function SignaturePad({
  onChange,
}: {
  onChange: (hasSignature: boolean) => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  function getCtx(): CanvasRenderingContext2D | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) {
      setHasDrawn(true);
      onChange(true);
    }
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange(false);
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={600}
        height={200}
        className="w-full rounded-lg border border-border bg-white touch-none"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      <button type="button" className="btn-secondary btn-sm gap-1.5" onClick={handleClear}>
        {t.invoiceConfirm.signatureClear}
      </button>
    </div>
  );
}

function signatureDataUrl(canvas: HTMLCanvasElement | null): string | null {
  if (!canvas) return null;
  return canvas.toDataURL('image/png');
}

// ── Error mapping ────────────────────────────────────────────────────────

function submitErrorMessage(status: number | undefined): string {
  switch (status) {
    case 403:
      return t.invoiceConfirm.errIdCardMismatch;
    case 410:
      return t.invoiceConfirm.errLinkExpired;
    case 409:
      return t.invoiceConfirm.errAlreadyConfirmed;
    case 400:
      return t.invoiceConfirm.errInvalidSignature;
    default:
      return t.invoiceConfirm.errGeneral;
  }
}

// ── Component ────────────────────────────────────────────────────────────

export function InvoiceConfirm(): JSX.Element {
  const { token } = useParams({ from: '/public-layout/invoice-confirm/$token' });

  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const [idCard, setIdCard] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [changeBank, setChangeBank] = useState(false);
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice-confirm', token],
    queryFn: () => invoiceConfirmApi.get(token),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof invoiceConfirmApi.confirm>[1]) =>
      invoiceConfirmApi.confirm(token, payload),
    onSuccess: () => {
      setSubmitted(true);
      setSubmitError(null);
    },
    onError: (err: unknown) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      setSubmitError(submitErrorMessage(status));
    },
  });

  const canSubmit = idCard.trim().length > 0 && hasSignature && !mutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);

    const canvas = canvasWrapRef.current?.querySelector('canvas') ?? null;
    const dataUrl = signatureDataUrl(canvas);
    if (!dataUrl) return;
    // Backend expects RAW base64 (no data-URI prefix) — it decodes via
    // decodeUpload('image/png', signatureBase64).
    const commaIdx = dataUrl.indexOf(',');
    const signatureBase64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;

    const payload: Parameters<typeof invoiceConfirmApi.confirm>[1] = {
      idCardNumber: idCard.trim(),
      signatureBase64,
    };
    if (changeBank) {
      const nameVal = bankAccountName.trim();
      const numVal = bankAccountNumber.trim();
      if (nameVal) payload.bankAccountName = nameVal;
      if (numVal) payload.bankAccountNumber = numVal;
    }

    mutation.mutate(payload);
  }

  // ── Render states ─────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Shell>
        <p className="text-center text-muted-foreground py-16 animate-pulse">
          {t.invoiceConfirm.loadingForm}
        </p>
      </Shell>
    );
  }

  if (error || !data) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const title =
      status === 410 ? t.invoiceConfirm.expiredLinkTitle : t.invoiceConfirm.invalidLinkTitle;
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Icon name="cancel" size={40} className="text-destructive" />
          <p className="text-sm text-destructive font-medium">{title}</p>
        </div>
      </Shell>
    );
  }

  if (data.alreadyConfirmed) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="schedule" size={48} className="text-muted-foreground" />
          <h2 className="text-xl font-bold text-ink">{t.invoiceConfirm.alreadyConfirmedTitle}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t.invoiceConfirm.alreadyConfirmedBody(data.refNumber)}
          </p>
        </div>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Icon name="check_circle" size={48} className="text-[hsl(var(--success))]" />
          <h2 className="text-xl font-bold text-ink">{t.invoiceConfirm.successTitle}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">{t.invoiceConfirm.successBody}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6 pb-12">
        {/* Invoice summary */}
        <section className="card-paper p-5 space-y-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="mono-meta text-muted-foreground num">{data.refNumber}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {t.invoiceConfirm.vendor} <strong>{data.vendorName}</strong>
          </p>
        </section>

        {/* Items */}
        <section className="space-y-2">
          <div className="section-heading">
            <h2 className="status-label text-muted-foreground">
              {t.invoiceConfirm.itemsHeading(data.items.length)}
            </h2>
          </div>
          <div className="card-paper overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="text-start font-medium p-3">{t.invoiceConfirm.colItem}</th>
                  <th className="text-end font-medium p-3">{t.invoiceConfirm.colQty}</th>
                  <th className="text-start font-medium p-3">{t.invoiceConfirm.colUnit}</th>
                  <th className="text-end font-medium p-3">{t.invoiceConfirm.colUnitPrice}</th>
                  <th className="text-end font-medium p-3">{t.invoiceConfirm.colLineTotal}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, idx) => (
                  <tr key={idx} className="border-b border-border last:border-0">
                    <td className="p-3">{item.name}</td>
                    <td className="p-3 text-end num">{item.qty}</td>
                    <td className="p-3">{item.unit}</td>
                    <td className="p-3 text-end num">{item.unitPrice.toFixed(2)}</td>
                    <td className="p-3 text-end num font-medium">{item.lineTotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-paper p-4 flex justify-between items-center">
            <span className="text-sm font-medium">{t.invoiceConfirm.grandTotal}</span>
            <span className="font-bold num text-ink">
              {'MVR '}
              {data.grandTotal.toFixed(2)}
            </span>
          </div>
        </section>

        <form id="invoice-confirm-form" onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Bank account */}
          <section className="card-paper p-5 space-y-3">
            <h2 className="status-label text-muted-foreground">{t.invoiceConfirm.bankHeading}</h2>
            <div className="text-sm space-y-1">
              <p>
                {t.invoiceConfirm.bankAccountName}:{' '}
                <strong>{data.bankAccountName ?? t.invoiceConfirm.bankNotSet}</strong>
              </p>
              <p>
                {t.invoiceConfirm.bankAccountNumber}:{' '}
                <strong className="num" dir="ltr">
                  {data.bankAccountNumber ?? t.invoiceConfirm.bankNotSet}
                </strong>
              </p>
            </div>

            <button
              type="button"
              className="btn-secondary btn-sm gap-1.5"
              onClick={() => setChangeBank((v) => !v)}
            >
              {t.invoiceConfirm.changeBankToggle}
            </button>

            {changeBank && (
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <label htmlFor="bank-name" className="field-label">
                    {t.invoiceConfirm.newBankAccountName}
                  </label>
                  <input
                    id="bank-name"
                    type="text"
                    className="input text-base"
                    value={bankAccountName}
                    onChange={(e) => setBankAccountName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="bank-number" className="field-label">
                    {t.invoiceConfirm.newBankAccountNumber}
                  </label>
                  <input
                    id="bank-number"
                    type="text"
                    className="input text-base num"
                    value={bankAccountNumber}
                    onChange={(e) => setBankAccountNumber(e.target.value)}
                    autoComplete="off"
                    dir="ltr"
                  />
                </div>
              </div>
            )}
          </section>

          {/* ID card */}
          <section className="card-paper p-5 space-y-1.5">
            <label htmlFor="id-card" className="field-label">
              {t.invoiceConfirm.idCardLabel}
            </label>
            <input
              id="id-card"
              type="text"
              className="input text-base num"
              placeholder={t.invoiceConfirm.idCardPlaceholder}
              value={idCard}
              onChange={(e) => setIdCard(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              dir="ltr"
            />
          </section>

          {/* Signature */}
          <section className="card-paper p-5 space-y-2">
            <h2 className="status-label text-muted-foreground">{t.invoiceConfirm.signatureHeading}</h2>
            <p className="text-xs text-muted-foreground">{t.invoiceConfirm.signatureHint}</p>
            <div ref={canvasWrapRef}>
              <SignaturePad onChange={setHasSignature} />
            </div>
          </section>

          {/* Error */}
          {submitError && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            >
              {submitError}
            </div>
          )}

          {/* Submit */}
          <div className="card-paper p-5">
            <button
              type="submit"
              form="invoice-confirm-form"
              disabled={!canSubmit}
              className="btn-primary w-full py-3 text-base"
            >
              {mutation.isPending ? t.invoiceConfirm.submitting : t.invoiceConfirm.submit}
            </button>
          </div>
        </form>
      </div>
    </Shell>
  );
}

// ── Standalone shell (no sidebar/header) ─────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="dock sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-ink truncate">WapPro</p>
          <p className="text-xs text-muted-foreground">{t.invoiceConfirm.pageTitle}</p>
        </div>
      </header>
      <main className="mx-auto w-full max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}
